import { useEffect, useMemo, useState, type CSSProperties, type ReactNode } from 'react';
import { Icon, tokens } from '@/design';
import { DEFAULT_SCALE_BAR_TEXT_SYMBOL } from './layoutDocument';
import { SymbolColorPicker } from './SymbolColorPicker';
import type { ScaleBarTextSymbol } from './layoutTypes';

export interface ScaleBarTextSymbolPanelProps {
  readonly value: ScaleBarTextSymbol;
  readonly onApply: (value: ScaleBarTextSymbol) => void;
  readonly onBack: () => void;
  readonly heading?: string;
  readonly testId?: string;
}

type SectionId =
  | 'appearance'
  | 'position'
  | 'rotation'
  | 'halo'
  | 'shadow'
  | 'callout'
  | 'output';

const FONTS = [
  'Arial',
  'Calibri',
  'Inter',
  'Georgia',
  'Times New Roman',
  'Courier New',
] as const;

function normalized(value: ScaleBarTextSymbol): ScaleBarTextSymbol {
  return {
    ...DEFAULT_SCALE_BAR_TEXT_SYMBOL,
    ...value,
    halo: { ...DEFAULT_SCALE_BAR_TEXT_SYMBOL.halo, ...value.halo },
    shadow: { ...DEFAULT_SCALE_BAR_TEXT_SYMBOL.shadow, ...value.shadow },
    callout: { ...DEFAULT_SCALE_BAR_TEXT_SYMBOL.callout, ...value.callout },
  };
}

function Section({
  id,
  title,
  open,
  onToggle,
  children,
}: {
  readonly id: SectionId;
  readonly title: string;
  readonly open: boolean;
  readonly onToggle: (id: SectionId) => void;
  readonly children: ReactNode;
}) {
  return (
    <section>
      <button
        type="button"
        aria-label={`${open ? 'Collapse' : 'Expand'} text ${title}`}
        aria-expanded={open}
        onClick={() => onToggle(id)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: tokens.space[1],
          width: '100%',
          minHeight: tokens.density.rowDense,
          padding: 0,
          border: 0,
          background: 'transparent',
          color: tokens.color.ink.primary,
          cursor: 'pointer',
          fontFamily: tokens.font.family.ui,
          fontSize: tokens.font.size.md,
          fontWeight: tokens.font.weight.semibold,
          textAlign: 'left',
        }}
      >
        <Icon name={open ? 'expand_more' : 'chevron_right'} size={16} decorative />
        {title}
      </button>
      {open ? (
        <div
          style={{
            display: 'grid',
            gap: tokens.space[2],
            padding: `0 0 ${tokens.space[3]}px ${tokens.space[5]}px`,
          }}
        >
          {children}
        </div>
      ) : null}
    </section>
  );
}

function bounded(
  value: string,
  min: number,
  max: number,
  fallback: number,
): number {
  const parsed = Number(value);
  return Number.isFinite(parsed)
    ? Math.max(min, Math.min(max, parsed))
    : fallback;
}

export function ScaleBarTextSymbolPanel({
  value,
  onApply,
  onBack,
  heading = 'Units',
  testId = 'scale-bar-text-symbol-panel',
}: ScaleBarTextSymbolPanelProps) {
  const committed = useMemo(() => normalized(value), [value]);
  const [draft, setDraft] = useState(committed);
  const [autoApply, setAutoApply] = useState(false);
  const [openSections, setOpenSections] = useState<ReadonlySet<SectionId>>(
    () => new Set<SectionId>(['appearance', 'position', 'rotation']),
  );

  useEffect(() => setDraft(committed), [committed]);

  const dirty = JSON.stringify(draft) !== JSON.stringify(committed);
  const edit = (patch: Partial<ScaleBarTextSymbol>) => {
    const next = { ...draft, ...patch };
    setDraft(next);
    if (autoApply) onApply(next);
  };
  const editHalo = (patch: Partial<ScaleBarTextSymbol['halo']>) =>
    edit({ halo: { ...draft.halo, ...patch } });
  const editShadow = (patch: Partial<ScaleBarTextSymbol['shadow']>) =>
    edit({ shadow: { ...draft.shadow, ...patch } });
  const editCallout = (patch: Partial<ScaleBarTextSymbol['callout']>) =>
    edit({ callout: { ...draft.callout, ...patch } });
  const toggle = (id: SectionId) =>
    setOpenSections((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const field: CSSProperties = {
    minHeight: tokens.density.rowDense,
    width: '100%',
    boxSizing: 'border-box',
    padding: `0 ${tokens.space[2]}px`,
    border: tokens.border.thin,
    borderRadius: tokens.radius.input,
    background: tokens.color.surface[0],
    color: tokens.color.ink.primary,
    fontFamily: tokens.font.family.ui,
    fontSize: tokens.font.size.sm,
  };
  const row: CSSProperties = {
    display: 'grid',
    gridTemplateColumns: `${tokens.density.touchMin * 2.7}px minmax(0, 1fr)`,
    alignItems: 'center',
    gap: tokens.space[2],
    fontSize: tokens.font.size.sm,
  };
  const check: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.space[2],
    fontSize: tokens.font.size.sm,
  };
  const alignmentButton = (active: boolean): CSSProperties => ({
    minWidth: tokens.density.rowDense,
    minHeight: tokens.density.rowDense,
    padding: 0,
    border: tokens.border.thin,
    borderRadius: tokens.radius.sm,
    background: active ? tokens.color.accent.subtle : tokens.color.surface[0],
    color: active ? tokens.color.accent.primary : tokens.color.ink.secondary,
    cursor: 'pointer',
  });
  const action = (enabled: boolean): CSSProperties => ({
    minHeight: tokens.density.rowDense,
    padding: `0 ${tokens.space[3]}px`,
    border: tokens.border.thin,
    borderRadius: tokens.radius.input,
    background: tokens.color.surface[0],
    color: enabled ? tokens.color.ink.primary : tokens.color.ink.muted,
    cursor: enabled ? 'pointer' : 'not-allowed',
    fontFamily: tokens.font.family.ui,
  });

  return (
    <div data-testid={testId}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: tokens.space[2],
          paddingBottom: tokens.space[2],
          borderBottom: tokens.border.thin,
        }}
      >
        <button
          type="button"
          aria-label="Back to scale bar properties"
          onClick={onBack}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: tokens.density.rowDense,
            minHeight: tokens.density.rowDense,
            border: 0,
            borderRadius: tokens.radius.pill,
            background: 'transparent',
            color: tokens.color.ink.secondary,
            cursor: 'pointer',
          }}
        >
          <Icon name="arrow_back" size={20} decorative />
        </button>
        <strong
          style={{
            color: tokens.color.semantic.info,
            fontSize: tokens.font.size.md,
          }}
        >
          {heading}
        </strong>
      </div>

      <div
        aria-hidden
        style={{
          display: 'flex',
          gap: tokens.space[2],
          padding: `${tokens.space[2]}px 0`,
          borderBottom: tokens.border.thin,
          color: tokens.color.semantic.info,
        }}
      >
        <Icon name="format_color_fill" size={24} decorative />
        <Icon name="text_fields" size={24} decorative />
      </div>

      <Section
        id="appearance"
        title="Appearance"
        open={openSections.has('appearance')}
        onToggle={toggle}
      >
        <label style={row}>
          <span>Font name</span>
          <select
            aria-label="Scale bar font name"
            value={draft.fontFamily}
            onChange={(event) => edit({ fontFamily: event.target.value })}
            style={field}
          >
            {FONTS.map((font) => <option key={font}>{font}</option>)}
          </select>
        </label>
        <label style={row}>
          <span>Font style</span>
          <select
            aria-label="Scale bar font style"
            value={draft.fontStyle}
            onChange={(event) =>
              edit({ fontStyle: event.target.value as ScaleBarTextSymbol['fontStyle'] })
            }
            style={field}
          >
            <option value="regular">Regular</option>
            <option value="italic">Italic</option>
            <option value="bold">Bold</option>
            <option value="boldItalic">Bold Italic</option>
          </select>
        </label>
        <label style={row}>
          <span>Size</span>
          <input
            aria-label="Scale bar text size"
            type="number"
            min={4}
            max={72}
            step={0.5}
            value={draft.sizePt}
            onChange={(event) =>
              edit({ sizePt: bounded(event.target.value, 4, 72, draft.sizePt) })
            }
            style={field}
          />
        </label>
        <div style={row}>
          <span>Color</span>
          <SymbolColorPicker
            label="Scale bar advanced text color"
            value={draft.color}
            allowNoColor={false}
            onChange={(color) => edit({ color: color ?? draft.color })}
          />
        </div>
        <div style={row}>
          <span>Outline color</span>
          <SymbolColorPicker
            label="Scale bar text outline color"
            value={draft.outlineColor}
            allowNoColor={false}
            onChange={(color) => edit({ outlineColor: color ?? draft.outlineColor })}
          />
        </div>
        <label style={row}>
          <span>Outline width</span>
          <input
            aria-label="Scale bar text outline width"
            type="number"
            min={0}
            max={12}
            step={0.25}
            value={draft.outlineWidthPt}
            onChange={(event) =>
              edit({
                outlineWidthPt: bounded(
                  event.target.value,
                  0,
                  12,
                  draft.outlineWidthPt,
                ),
              })
            }
            style={field}
          />
        </label>
        <label style={check}>
          <input
            type="checkbox"
            aria-label="Underline scale bar text"
            checked={draft.underline}
            onChange={(event) => edit({ underline: event.target.checked })}
          />
          Underline
        </label>
        <label style={check}>
          <input
            type="checkbox"
            aria-label="Strikethrough scale bar text"
            checked={draft.strikethrough}
            onChange={(event) => edit({ strikethrough: event.target.checked })}
          />
          Strikethrough
        </label>
        <label style={row}>
          <span>Text case</span>
          <select
            aria-label="Scale bar text case"
            value={draft.textCase}
            onChange={(event) =>
              edit({ textCase: event.target.value as ScaleBarTextSymbol['textCase'] })
            }
            style={field}
          >
            <option value="normal">Normal</option>
            <option value="uppercase">Uppercase</option>
            <option value="lowercase">Lowercase</option>
            <option value="smallCaps">Small caps</option>
          </select>
        </label>
        <label style={row}>
          <span>Position adjustment</span>
          <select
            aria-label="Scale bar text position adjustment"
            value={draft.positionAdjustment}
            onChange={(event) =>
              edit({
                positionAdjustment:
                  event.target.value as ScaleBarTextSymbol['positionAdjustment'],
              })
            }
            style={field}
          >
            <option value="normal">Normal</option>
            <option value="superscript">Superscript</option>
            <option value="subscript">Subscript</option>
          </select>
        </label>
      </Section>

      <Section
        id="position"
        title="Position"
        open={openSections.has('position')}
        onToggle={toggle}
      >
        <div style={row}>
          <span>Horizontal alignment</span>
          <div style={{ display: 'flex', gap: tokens.space[1] }}>
            {(['left', 'center', 'right', 'justify'] as const).map((alignment) => (
              <button
                key={alignment}
                type="button"
                aria-label={`Text horizontal ${alignment}`}
                aria-pressed={draft.horizontalAlignment === alignment}
                onClick={() => edit({ horizontalAlignment: alignment })}
                style={alignmentButton(draft.horizontalAlignment === alignment)}
              >
                {alignment === 'left' ? '≡'
                  : alignment === 'center' ? '☰'
                    : alignment === 'right' ? '≣'
                      : '▤'}
              </button>
            ))}
          </div>
        </div>
        <div style={row}>
          <span>Vertical alignment</span>
          <div style={{ display: 'flex', gap: tokens.space[1] }}>
            {(['top', 'middle', 'baseline', 'bottom'] as const).map((alignment) => (
              <button
                key={alignment}
                type="button"
                aria-label={`Text vertical ${alignment}`}
                aria-pressed={draft.verticalAlignment === alignment}
                onClick={() => edit({ verticalAlignment: alignment })}
                style={alignmentButton(draft.verticalAlignment === alignment)}
              >
                {alignment.slice(0, 1).toUpperCase()}
              </button>
            ))}
          </div>
        </div>
        <label style={row}>
          <span>Offset X</span>
          <input
            aria-label="Scale bar text offset X"
            type="number"
            min={-72}
            max={72}
            step={0.5}
            value={draft.offsetXPt}
            onChange={(event) =>
              edit({ offsetXPt: bounded(event.target.value, -72, 72, draft.offsetXPt) })
            }
            style={field}
          />
        </label>
        <label style={row}>
          <span>Offset Y</span>
          <input
            aria-label="Scale bar text offset Y"
            type="number"
            min={-72}
            max={72}
            step={0.5}
            value={draft.offsetYPt}
            onChange={(event) =>
              edit({ offsetYPt: bounded(event.target.value, -72, 72, draft.offsetYPt) })
            }
            style={field}
          />
        </label>
      </Section>

      <Section
        id="rotation"
        title="Rotation"
        open={openSections.has('rotation')}
        onToggle={toggle}
      >
        <label style={row}>
          <span>Angle</span>
          <input
            aria-label="Scale bar text angle"
            type="number"
            min={-360}
            max={360}
            step={1}
            value={draft.angle}
            onChange={(event) =>
              edit({ angle: bounded(event.target.value, -360, 360, draft.angle) })
            }
            style={field}
          />
        </label>
      </Section>

      <Section
        id="halo"
        title="Halo"
        open={openSections.has('halo')}
        onToggle={toggle}
      >
        <label style={check}>
          <input
            type="checkbox"
            aria-label="Enable scale bar text halo"
            checked={draft.halo.enabled}
            onChange={(event) => editHalo({ enabled: event.target.checked })}
          />
          Enable halo
        </label>
        <div style={row}>
          <span>Color</span>
          <SymbolColorPicker
            label="Scale bar text halo color"
            value={draft.halo.color}
            allowNoColor={false}
            disabled={!draft.halo.enabled}
            onChange={(color) => editHalo({ color: color ?? draft.halo.color })}
          />
        </div>
        <label style={row}>
          <span>Size</span>
          <input
            aria-label="Scale bar text halo size"
            type="number"
            min={0}
            max={12}
            step={0.5}
            disabled={!draft.halo.enabled}
            value={draft.halo.sizePt}
            onChange={(event) =>
              editHalo({
                sizePt: bounded(event.target.value, 0, 12, draft.halo.sizePt),
              })
            }
            style={field}
          />
        </label>
      </Section>

      <Section
        id="shadow"
        title="Shadow"
        open={openSections.has('shadow')}
        onToggle={toggle}
      >
        <label style={check}>
          <input
            type="checkbox"
            aria-label="Enable scale bar text shadow"
            checked={draft.shadow.enabled}
            onChange={(event) => editShadow({ enabled: event.target.checked })}
          />
          Enable shadow
        </label>
        <div style={row}>
          <span>Color</span>
          <SymbolColorPicker
            label="Scale bar text shadow color"
            value={draft.shadow.color}
            allowNoColor={false}
            disabled={!draft.shadow.enabled}
            onChange={(color) => editShadow({ color: color ?? draft.shadow.color })}
          />
        </div>
        <label style={row}>
          <span>Offset X</span>
          <input
            aria-label="Scale bar text shadow offset X"
            type="number"
            min={-24}
            max={24}
            step={0.5}
            disabled={!draft.shadow.enabled}
            value={draft.shadow.offsetXPt}
            onChange={(event) =>
              editShadow({
                offsetXPt: bounded(
                  event.target.value,
                  -24,
                  24,
                  draft.shadow.offsetXPt,
                ),
              })
            }
            style={field}
          />
        </label>
        <label style={row}>
          <span>Offset Y</span>
          <input
            aria-label="Scale bar text shadow offset Y"
            type="number"
            min={-24}
            max={24}
            step={0.5}
            disabled={!draft.shadow.enabled}
            value={draft.shadow.offsetYPt}
            onChange={(event) =>
              editShadow({
                offsetYPt: bounded(
                  event.target.value,
                  -24,
                  24,
                  draft.shadow.offsetYPt,
                ),
              })
            }
            style={field}
          />
        </label>
      </Section>

      <Section
        id="callout"
        title="Callout"
        open={openSections.has('callout')}
        onToggle={toggle}
      >
        <label style={check}>
          <input
            type="checkbox"
            aria-label="Enable scale bar text callout"
            checked={draft.callout.enabled}
            onChange={(event) => editCallout({ enabled: event.target.checked })}
          />
          Background callout
        </label>
        <div style={row}>
          <span>Fill</span>
          <SymbolColorPicker
            label="Scale bar text callout fill"
            value={draft.callout.fillColor}
            allowNoColor={false}
            disabled={!draft.callout.enabled}
            onChange={(color) =>
              editCallout({ fillColor: color ?? draft.callout.fillColor })
            }
          />
        </div>
        <div style={row}>
          <span>Outline</span>
          <SymbolColorPicker
            label="Scale bar text callout outline"
            value={draft.callout.outlineColor}
            allowNoColor={false}
            disabled={!draft.callout.enabled}
            onChange={(color) =>
              editCallout({ outlineColor: color ?? draft.callout.outlineColor })
            }
          />
        </div>
        <label style={row}>
          <span>Padding</span>
          <input
            aria-label="Scale bar text callout padding"
            type="number"
            min={0}
            max={24}
            step={0.5}
            disabled={!draft.callout.enabled}
            value={draft.callout.paddingPt}
            onChange={(event) =>
              editCallout({
                paddingPt: bounded(
                  event.target.value,
                  0,
                  24,
                  draft.callout.paddingPt,
                ),
              })
            }
            style={field}
          />
        </label>
      </Section>

      <Section
        id="output"
        title="Output"
        open={openSections.has('output')}
        onToggle={toggle}
      >
        <label style={row}>
          <span>Opacity</span>
          <input
            aria-label="Scale bar text opacity"
            type="range"
            min={0}
            max={100}
            value={draft.opacityPercent}
            onChange={(event) =>
              edit({
                opacityPercent: bounded(
                  event.target.value,
                  0,
                  100,
                  draft.opacityPercent,
                ),
              })
            }
          />
        </label>
        <output aria-label="Scale bar text opacity value">
          {draft.opacityPercent}%
        </output>
      </Section>

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: tokens.space[2],
          marginTop: tokens.space[2],
          paddingTop: tokens.space[2],
          borderTop: tokens.border.thin,
        }}
      >
        <label style={check}>
          <input
            type="checkbox"
            role="switch"
            aria-label="Auto apply scale bar text changes"
            checked={autoApply}
            onChange={(event) => {
              setAutoApply(event.target.checked);
              if (event.target.checked && dirty) onApply(draft);
            }}
          />
          Auto Apply
        </label>
        <div style={{ display: 'flex', gap: tokens.space[2] }}>
          <button
            type="button"
            aria-label="Apply scale bar text changes"
            disabled={!dirty}
            onClick={() => onApply(draft)}
            style={action(dirty)}
          >
            Apply
          </button>
          <button
            type="button"
            aria-label="Cancel scale bar text changes"
            disabled={!dirty}
            onClick={() => setDraft(committed)}
            style={action(dirty)}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
